// src/pages/salary/components/PrintablePayslip.jsx
import { getMonthName } from '../config/translations_Salary';

const PrintablePayslip = ({ payrollData, employee, theme, t, isRTL, selectedMonth, selectedYear, calculatedValues }) => {
  const formattedName = (payrollData?.employeeName || employee?.arabicName || '')
    .toString().split(/\s+/).slice(0, 3).join(' ');

  // ── same JSX as original, unchanged ──
  return (
    <div className="printable-payslip" style={{ display: 'none' }}>
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 14mm;
          }
          body {
            margin: 0;
            color: #111;
            font: 12pt/1.4 Arial, sans-serif;
          }

          .table-page {
            break-inside: avoid-page;
            page-break-inside: avoid;
          }

          .table-page + .table-page {
            break-before: page;
            page-break-before: always;
          }

          .table-title {
            margin: 0 0 8mm;
            break-after: avoid;
            page-break-after: avoid;
          }

          .print-table {
            width: 100% !important;
            break-inside: avoid-page;
            page-break-inside: avoid;
          }

          .print-table thead {
            display: table-header-group;
          }

          .print-table tbody {
            display: table-row-group;
          }

          .print-table tfoot {
            display: table-footer-group;
          }

          .print-table tr,
          .print-table th,
          .print-table td {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .print-table th,
          .print-table td {
            border: 1px solid #d0d0d0;
            padding: 8px 10px;
            vertical-align: top;
          }

          .print-table thead th {
            background: #f3f6fa;
            font-weight: 700;
          }
        }
      `}</style>
      <div style={{ padding: '40px', fontFamily: 'Arial, sans-serif', direction: isRTL ? 'rtl' : 'ltr', maxWidth: '800px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '3px solid #2e7cba', paddingBottom: '20px', marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', color: '#2e7cba' }}>{t.payslipFor}</h1>
          <h2 style={{ margin: '0', fontSize: '20px', color: '#666' }}>{getMonthName(selectedMonth, t)} {selectedYear}</h2>
        </div>

        {/* Employee Info */}
        <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#2e7cba', fontSize: '18px' }}>{t.employeeInfo}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '30%' }}>{t.employeeCode}:</td>
                <td style={{ padding: '8px' }}>{payrollData.clientCode || payrollData.infinityCode || employee?.clientCode || employee?.infinityCode}</td>
                <td style={{ padding: '8px', fontWeight: 'bold', width: '30%' }}>{isRTL ? 'الاسم' : 'Name'}:</td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formattedName}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t.payrollPeriod}:</td>
                <td style={{ padding: '8px' }} colSpan="3">
                  {payrollData.payrollStartDate?.split('T')[0]} {t.to} {payrollData.payrollEndDate?.split('T')[0]}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Salary Table */}
        <div className="table-page" style={{ marginBottom: '30px', pageBreakInside: 'avoid' }}>
          <h3 className="table-title" style={{ margin: '0 0 15px 0', color: '#2e7cba', fontSize: '18px', fontWeight: 'bold', borderBottom: '3px solid #2e7cba', paddingBottom: '12px', textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.5px' }}>{t.salaryComposition}</h3>
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <tbody>
              <tr style={{ backgroundColor: '#f9f9f9' }}>
                <td style={{ padding: '12px', fontWeight: 'bold', border: '1px solid #ddd' }}>{t.basicSalary}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd' }}>{parseFloat(payrollData.basicSalary).toFixed(2)} {t.egp}</td>
              </tr>
              {(payrollData.transportationAllowances > 0 || payrollData.transpotartionAllowances > 0) && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.transportationAllowance}</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: 'green' }}>
                    + {(payrollData.transportationAllowances || payrollData.transpotartionAllowances || 0).toFixed(2)} {t.egp}
                  </td>
                </tr>
              )}
              {payrollData.variableSalary > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.variableSalaryAllowance}</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: 'green' }}>+ {parseFloat(payrollData.variableSalary).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {payrollData.overtimeValue > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.overtimeAllowance} ({payrollData.overtimeHours} {isRTL ? 'ساعة' : 'hrs'})</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: 'green' }}>+ {parseFloat(payrollData.overtimeValue).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {payrollData.bonusValue > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.bonusAllowance}</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: 'green' }}>+ {parseFloat(payrollData.bonusValue).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {payrollData.expenses > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.expensesAllowance}</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: 'green' }}>+ {parseFloat(payrollData.expenses).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              <tr style={{ backgroundColor: '#e8f4f8', fontWeight: 'bold' }}>
                <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.grossSalary}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd' }}>{parseFloat(payrollData.grossSalary).toFixed(2)} {t.egp}</td>
              </tr>
              <tr style={{ backgroundColor: '#fff3cd' }}>
                <td style={{ padding: '12px', fontWeight: 'bold', border: '1px solid #ddd', color: '#a71d2a' }} colSpan="2">{t.deductionsBreakdown}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.incomeTax}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.monthlyTax).toFixed(2)} {t.egp}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.socialInsurance}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(calculatedValues?.socialInsurance || 0).toFixed(2)} {t.egp}</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.martyrsFund}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.martyrsFund).toFixed(2)} {t.egp}</td>
              </tr>
              {payrollData.absenceDaysValue > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.absentDays} ({payrollData.absenceDays} {isRTL ? 'يوم' : 'days'})</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.absenceDaysValue).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {payrollData.penalityDaysValue > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.penalties} ({payrollData.penalityDays} {isRTL ? 'يوم' : 'days'})</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.penalityDaysValue).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {payrollData.sickLeave25DeductionValue > 0 && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.sickLeave} 25% ({payrollData.sickLeave25DeductionDays} {isRTL ? 'يوم' : 'days'})</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.sickLeave25DeductionValue).toFixed(2)} {t.egp}</td>
                </tr>
              )}
              {Array.isArray(payrollData.deductions) && payrollData.deductions.some((d) => d.type === 'advancement') && (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd' }}>{isRTL ? 'سلفة' : 'Advancement (Solfa)'}</td>
                  <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>
                    - {payrollData.deductions.filter((d) => d.type === 'advancement').reduce((s, d) => s + Number(d.amount || 0), 0).toFixed(2)} {t.egp}
                  </td>
                </tr>
              )}
              <tr style={{ backgroundColor: '#f8d7da', fontWeight: 'bold' }}>
                <td style={{ padding: '12px', border: '1px solid #ddd' }}>{t.totalDeductions}</td>
                <td style={{ padding: '12px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#a71d2a' }}>- {parseFloat(payrollData.totalDeductions).toFixed(2)} {t.egp}</td>
              </tr>
              <tr style={{ backgroundColor: '#d4edda', fontWeight: 'bold', fontSize: '18px', borderTop: '3px solid #2e7cba' }}>
                <td style={{ padding: '15px', border: '1px solid #ddd' }}>{t.netSalary}</td>
                <td style={{ padding: '15px', textAlign: isRTL ? 'left' : 'right', border: '1px solid #ddd', color: '#2e7cba' }}>{parseFloat(payrollData.netSalary).toFixed(2)} {t.egp}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Allowance Instances - New Page */}
        <div className="table-page pdf-section-start allowance-instances-print-section" style={{ pageBreakBefore: 'always', pageBreakInside: 'avoid', marginBottom: '30px' }}>
          <h3 className="table-title" style={{ margin: '0 0 15px 0', color: '#2e7cba', fontSize: '18px', fontWeight: 'bold', borderBottom: '3px solid #2e7cba', paddingBottom: '12px', textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.5px' }}>
            {t.allowanceInstances}
          </h3>
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', pageBreakInside: 'avoid' }}>
            <thead>
              <tr style={{ backgroundColor: '#e8f4f8', borderBottom: '3px solid #2e7cba' }}>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', color: '#2e7cba' }}>{t.type}</th>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', color: '#2e7cba' }}>{t.description}</th>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold', color: '#2e7cba' }}>{t.amount} ({t.egp})</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(payrollData.allowances) && payrollData.allowances.length > 0 ? (
                <>
                  {payrollData.allowances.map((row, idx) => {
                    const meta = [];
                    if (row.days != null && row.days !== '') meta.push(`${row.days} ${t.days}`);
                    if (row.hours != null && row.hours !== '') meta.push(`${row.hours} ${t.hours}`);
                    const description = [row.description, meta.join(' · ')].filter(Boolean).join(' — ');
                    return (
                      <tr key={row.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9f9f9', pageBreakInside: 'avoid' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd', fontSize: '14px' }}>{row.type || '-'}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', color: '#555', fontSize: '14px' }}>{description || '-'}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', color: '#16a34a', fontWeight: '600', fontSize: '14px' }}>
                          +{(Number(row.amount) || 0).toFixed(2)} {t.egp}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#e8f4f8', fontWeight: 'bold', borderTop: '2px solid #2e7cba', pageBreakInside: 'avoid' }}>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#2e7cba' }} colSpan={2}>{t.total}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', color: '#16a34a', fontSize: '15px' }}>
                      +{payrollData.allowances.reduce((s, r) => s + (Number(r.amount) || 0), 0).toFixed(2)} {t.egp}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center', color: '#999', fontSize: '14px' }} colSpan={3}>
                    {t.noAllowances}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Deduction Instances - New Page */}
        <div className="table-page pdf-section-start deduction-instances-print-section" style={{ pageBreakBefore: 'always', pageBreakInside: 'avoid', marginBottom: '30px' }}>
          <h3 className="table-title" style={{ margin: '0 0 15px 0', color: '#dc2626', fontSize: '18px', fontWeight: 'bold', borderBottom: '3px solid #dc2626', paddingBottom: '12px', textTransform: isRTL ? 'none' : 'uppercase', letterSpacing: isRTL ? 'normal' : '0.5px' }}>
            {t.deductionInstances}
          </h3>
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', pageBreakInside: 'avoid' }}>
            <thead>
              <tr style={{ backgroundColor: '#fdecea', borderBottom: '3px solid #dc2626' }}>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', color: '#dc2626' }}>{t.type}</th>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold', color: '#dc2626' }}>{t.description}</th>
                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold', color: '#dc2626' }}>{t.amount} ({t.egp})</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(payrollData.deductions) && payrollData.deductions.length > 0 ? (
                <>
                  {payrollData.deductions.map((row, idx) => {
                    const meta = [];
                    if (row.days != null && row.days !== '') meta.push(`${row.days} ${t.days}`);
                    if (row.hours != null && row.hours !== '') meta.push(`${row.hours} ${t.hours}`);
                    const description = [row.description, meta.join(' · ')].filter(Boolean).join(' — ');
                    return (
                      <tr key={row.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#faf9f9', pageBreakInside: 'avoid' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd', fontSize: '14px' }}>{row.type || '-'}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', color: '#555', fontSize: '14px' }}>{description || '-'}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', color: '#dc2626', fontWeight: '600', fontSize: '14px' }}>
                          -{(Number(row.amount) || 0).toFixed(2)} {t.egp}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#fdecea', fontWeight: 'bold', borderTop: '2px solid #dc2626', pageBreakInside: 'avoid' }}>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#dc2626' }} colSpan={2}>{t.total}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: isRTL ? 'left' : 'right', color: '#dc2626', fontSize: '15px' }}>
                      -{payrollData.deductions.reduce((s, r) => s + (Number(r.amount) || 0), 0).toFixed(2)} {t.egp}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center', color: '#999', fontSize: '14px' }} colSpan={3}>
                    {t.noDeductionsRow}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '50px', paddingTop: '20px', borderTop: '3px solid #2e7cba', fontSize: '12px', color: '#666', textAlign: 'center' }}>
          <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#2e7cba' }}>{isRTL ? 'تم الإنشاء في' : 'Generated on'}: {new Date().toLocaleDateString(isRTL ? 'ar-EG' : 'en-US')} {new Date().toLocaleTimeString(isRTL ? 'ar-EG' : 'en-US')}</p>
          <p style={{ margin: '5px 0', fontSize: '11px', fontStyle: 'italic' }}>{isRTL ? 'هذا المستند إلكتروني ولا يتطلب توقيعًا' : 'This is an electronically generated document and does not require a signature'}</p>
        </div>
      </div>
    </div>
  );
};

export default PrintablePayslip;